import React from 'react';
import { Alert, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { RotatingLoader } from '../common/ui/RotatingLoader';
import { GrowConnectStateType, useGrowConnectState } from './useGrowConnectState';
import { GrowConnectCard } from './components/GrowConnectCard';
import { GrowConnectIntro } from './components/GrowConnectIntro';
import { GrowConnectForm } from './components/GrowConnectForm';
import { GrowConnectPending } from './components/GrowConnectPending';
import { GrowConnectApproved } from './components/GrowConnectApproved';
import { GrowConnectBlocked } from './components/GrowConnectBlocked';

export const GrowConnectPage = () => {
  const { t } = useTranslation();
  const { type, registration, existingProvider, refetchRegistration, refetchAccount } = useGrowConnectState();

  const onStale = async () => {
    await Promise.all([refetchAccount(), refetchRegistration()]);
  };

  // The option does not exist for a non-Israeli account, so a deep link should not either.
  if (type === GrowConnectStateType.INELIGIBLE) {
    return <Navigate to="/dashboard" replace />;
  }

  switch (type) {
    case GrowConnectStateType.LOADING:
    case GrowConnectStateType.APPROVED_FINALISING:
      return (
        <GrowConnectCard>
          <Grid container justifyContent="center">
            <RotatingLoader />
          </Grid>
        </GrowConnectCard>
      );
    case GrowConnectStateType.NOT_ADMIN:
      return (
        <GrowConnectCard title={t('growConnect.title')}>
          <Alert severity="info">{t('growConnect.adminOnly')}</Alert>
        </GrowConnectCard>
      );
    case GrowConnectStateType.STATUS_UNAVAILABLE:
      return (
        <GrowConnectCard title={t('growConnect.title')}>
          <Alert severity="error">{t('growConnect.errors.statusUnavailable')}</Alert>
        </GrowConnectCard>
      );
    case GrowConnectStateType.CONNECTED:
      return <GrowConnectApproved isConnected />;
    case GrowConnectStateType.APPROVED_NOT_CONNECTED:
      return <GrowConnectApproved isConnected={false} />;
    case GrowConnectStateType.PENDING:
      return registration ? (
        <GrowConnectPending registration={registration} onRegistered={refetchRegistration} onStale={onStale} />
      ) : null;
    case GrowConnectStateType.BLOCKED_NO_STOREFRONT:
      return <GrowConnectBlocked />;
    default:
      return (
        <GrowConnectCard title={t('growConnect.title')}>
          <GrowConnectIntro />
          {/* Grow never sets `rejected` today, but if it ever arrives it carries their note. */}
          {registration?.message && <Alert severity="info">{registration.message}</Alert>}
          <GrowConnectForm existingProvider={existingProvider} onRegistered={refetchRegistration} onStale={onStale} />
        </GrowConnectCard>
      );
  }
};
